/**
 * Aptitud de una serie para MPR 2D ortogonal.
 * @typedef {{ ok: boolean, code?: string, message?: string, warnings?: string[], plane?: string }} MprViability
 */

import {
  getDicomColumns,
  getDicomEchoNumber,
  getDicomPixelSpacing,
  getDicomRows,
  getDicomSliceDistance,
  getDicomSliceThickness,
  getDicomSpacingBetweenSlice,
  hasDicomDataSet,
} from '../functions'
import { isScoutFileItem } from './isScoutSeries'

const MIN_SLICES = 3

function parseIop(image) {
  if (!hasDicomDataSet(image)) return null
  const raw = image.data.string('x00200037')
  if (!raw) return null
  const parts = raw.split('\\').map((v) => parseFloat(v))
  if (parts.length < 6 || parts.some((n) => Number.isNaN(n))) return null
  return parts
}

function planeFromIop(iop) {
  const v = iop.map((x) => Math.round(x))
  let p = [
    v[1] * v[5] - v[2] * v[4],
    v[2] * v[3] - v[0] * v[5],
    v[0] * v[4] - v[1] * v[3],
  ].map((x) => Math.abs(x))
  if (p[0] === 1) return 'sagittal'
  if (p[1] === 1) return 'coronal'
  if (p[2] === 1) return 'axial'
  return ''
}

function iopKey(iop) {
  return iop.map((x) => Math.round(x * 1000) / 1000).join(',')
}

function fail(code, message) {
  return { ok: false, code, message, warnings: [] }
}

function getImageFromFile(f) {
  return f && f.image ? f.image : null
}

/**
 * @param {Array} files stack de la serie activa
 * @param {{ renderMode?: string }} [opts]
 * @returns {MprViability}
 */
export function assessMprViability(files, opts = {}) {
  const warnings = []
  if (!Array.isArray(files) || files.length === 0) {
    return fail('too_few_slices', 'No hay imágenes en la serie para MPR.')
  }

  const withImage = files.filter((f) => getImageFromFile(f) && hasDicomDataSet(getImageFromFile(f)))
  const onlyPreview =
    files.length > 0 &&
    files.every((f) => f && f.renderKind === 'preview') &&
    withImage.length === 0

  if (onlyPreview) {
    return fail(
      'preview_only',
      'Esta serie está en modo vista previa. Activá modo diagnóstico para usar MPR.'
    )
  }

  // Stack aún sin decodificar (manifest skeleton): permitir UI pero avisar
  if (withImage.length === 0 && files.length >= MIN_SLICES) {
    return {
      ok: true,
      code: 'pending_decode',
      message: '',
      warnings: ['La serie aún se está cargando; al activar MPR se decodificarán los cortes.'],
      plane: '',
    }
  }

  if (files.length < MIN_SLICES && withImage.length < MIN_SLICES) {
    // Un solo SOP multi-frame: no es volumen espacial en v1
    const first = getImageFromFile(files[0])
    const nFrames =
      first && first.data && typeof first.data.intString === 'function'
        ? parseInt(first.data.intString('x00280008') || '1', 10)
        : 1
    if (files.length === 1 && nFrames > 1) {
      return fail(
        'multiframe_not_volume',
        'Esta serie es multi-frame (cine) y no admite MPR volumétrico.'
      )
    }
    return fail(
      'too_few_slices',
      'Se necesitan al menos 3 cortes espaciales para reconstruir MPR.'
    )
  }

  const sample = withImage.length > 0 ? withImage : files
  const firstFile = sample[0]
  const firstImg = getImageFromFile(firstFile)
  if (!firstImg) {
    return fail('preview_only', 'Aún no hay imágenes DICOM cargadas para evaluar MPR.')
  }

  if (!hasDicomDataSet(firstImg)) {
    return fail('not_dicom', 'MPR solo está disponible en imágenes DICOM con geometría.')
  }

  // Scout / Localizer: excluir del paquete de reconstrucción 3D/MPR
  if (isScoutFileItem(firstFile) || files.some((f) => isScoutFileItem(f))) {
    return fail(
      'scout_series',
      'Esta serie es Scout/Localizer y se excluye de la reconstrucción MPR/3D.'
    )
  }

  // Secondary capture / color sin geometría útil
  try {
    const sopClass = firstImg.data.string('x00080016') || ''
    if (sopClass.indexOf('1.2.840.10008.5.1.4.1.1.7') === 0) {
      return fail('color_secondary', 'Secondary Capture no permite MPR.')
    }
  } catch (e) {
    /* ignore */
  }

  const iop0 = parseIop(firstImg)
  if (!iop0) {
    return fail('no_iop', 'Falta orientación de imagen (IOP). No se puede calcular MPR.')
  }

  const plane = planeFromIop(iop0)
  if (!plane) {
    return fail(
      'non_orthogonal',
      'Orientación oblicua: MPR ortogonal no está disponible en esta versión.'
    )
  }

  const rowSp = parseFloat(getDicomPixelSpacing(firstImg, 0))
  const colSp = parseFloat(getDicomPixelSpacing(firstImg, 1))
  if (!rowSp || !colSp || Number.isNaN(rowSp) || Number.isNaN(colSp)) {
    return fail('no_pixel_spacing', 'Falta Pixel Spacing. No se puede calibrar el MPR.')
  }

  const spacingBetween = getDicomSpacingBetweenSlice(firstImg)
  const sliceDist0 = getDicomSliceDistance(firstImg)
  const hasZ =
    (spacingBetween != null && !Number.isNaN(spacingBetween) && spacingBetween > 0) ||
    (sliceDist0 != null && !Number.isNaN(sliceDist0))
  if (!hasZ && sample.length >= 2) {
    const d1 = getDicomSliceDistance(getImageFromFile(sample[1]))
    if (d1 == null || Number.isNaN(d1)) {
      return fail(
        'no_z_geometry',
        'No hay espaciado entre cortes ni posición (IPP) usable para MPR.'
      )
    }
  } else if (!hasZ) {
    return fail(
      'no_z_geometry',
      'No hay espaciado entre cortes ni posición (IPP) usable para MPR.'
    )
  }

  const refRows = getDicomRows(firstImg) || firstImg.rows
  const refCols = getDicomColumns(firstImg) || firstImg.columns
  const refIopKey = iopKey(iop0)

  const checkCount = Math.min(sample.length, 12)
  const step = Math.max(1, Math.floor(sample.length / checkCount))
  for (let i = 0; i < sample.length; i += step) {
    const img = getImageFromFile(sample[i])
    if (!img || !hasDicomDataSet(img)) continue
    const rows = getDicomRows(img) || img.rows
    const cols = getDicomColumns(img) || img.columns
    if (rows && refRows && rows !== refRows) {
      return fail('inconsistent_matrix', 'Los cortes tienen distinto tamaño de matriz.')
    }
    if (cols && refCols && cols !== refCols) {
      return fail('inconsistent_matrix', 'Los cortes tienen distinto tamaño de matriz.')
    }
    const iop = parseIop(img)
    if (iop && iopKey(iop) !== refIopKey) {
      const otherPlane = planeFromIop(iop)
      if (otherPlane && otherPlane !== plane) {
        return fail(
          'inconsistent_orientation',
          'La orientación cambia entre cortes; no es un volumen coherente para MPR.'
        )
      }
    }
  }

  const thickness = getDicomSliceThickness(firstImg)
  if (
    spacingBetween != null &&
    thickness != null &&
    spacingBetween > 0 &&
    thickness > 0 &&
    spacingBetween < thickness * 0.85
  ) {
    warnings.push(
      'El espaciado entre cortes es menor que el grosor; la reconstrucción puede verse degradada.'
    )
  }

  const zSp = spacingBetween || Math.abs(colSp)
  if (zSp > 0 && colSp > 0 && zSp / colSp > 4) {
    warnings.push('Anisotropía fuerte en Z: los planos reconstruidos tendrán menor resolución.')
  }

  const echoes = new Set()
  for (let i = 0; i < Math.min(sample.length, 40); i++) {
    const img = getImageFromFile(sample[i])
    if (!img) continue
    const echo = getDicomEchoNumber(img)
    if (echo != null && !Number.isNaN(echo)) echoes.add(echo)
  }
  if (echoes.size > 1) {
    warnings.push('Serie multi-eco: MPR usará solo el eco de la imagen activa.')
  }

  if (opts.renderMode === 'PREVIEW') {
    warnings.push('Modo vista previa activo: se pedirá modo diagnóstico al construir MPR.')
  }

  return {
    ok: true,
    code: 'ok',
    message: '',
    warnings,
    plane,
  }
}
